import { get, cloneDeep, has } from "lodash";
import moment from "moment";

/*
RSSI CHART
*/
let ephocDate = (input) => {
  let miliSecondsFromEpoc = (parseInt(input, 10) / 32768) * 1000;
  let date = new Date(0); //Date(0) represents the epoc date
  date.setUTCMilliseconds(miliSecondsFromEpoc);
  return date;
};

let getRSSIChartModel = (reports) => {
  // if($scope.reportable.type === 'chime') {
  //   date = $filter('formatDate')(report.timestamp, 'MM/DD/YYYY HH:mm:ss:SSS', chartTimezone);
  //   rssi = report.data.status.mgmnt_rssi;
  // }
  let isChime = get(reports, "[0].type", null) === "chime";
  let rssiPath = isChime ? "data.status.mgmnt_rssi" : "data.status.rssi";
  let datePath = isChime ? "timestamp" : "data.status.time";

  return reports
    .map((report) => {
      let rssi = get(report, rssiPath);
      if (typeof rssi === "undefined") return null;
      let time = get(report, datePath, null);
      let date;
      if (isChime) date = time;
      else date = ephocDate(time);
      return {
        name: moment(date).format("MM/DD/YYYY HH:mm:ss:SSS"),
        y: parseFloat(rssi),
      };
    })
    .filter((rssi) => rssi !== null);
};

/*
STREAM CHART
*/
let getStreamChartModel = (reports) => {
  return reports
    .map((report) => {
      let hasStreamProfile = get(report, "data.statistics", {}).hasOwnProperty(
        "end_stream_profile"
      );
      if (hasStreamProfile) {
        let streamProfile = get(report, "data.statistics.end_stream_profile");
        let time =
          get(report, "data.statistics.sip_connect_time") ||
          get(report, "data.statistics.wake_up_time");
        let streamChunk = Math.abs(parseFloat(streamProfile));
        let date = ephocDate(time);
        return {
          name: moment(date).format("MM/DD/YYYY HH:mm:ss:SSS"),
          y: streamChunk,
        };
      } else return null;
    })
    .filter((stream) => stream !== null);
};

/*
BATTERY CHART
*/

const QRBP_V2_FAMILY = "qrbpv2";

let isQrbpV2 = (family) =>
  String(family || "").toLowerCase() === QRBP_V2_FAMILY;

let familyFromHealth = (deviceHealth, batteryNumber) => {
  let batteries = get(deviceHealth, "batteries", []);
  if (!Array.isArray(batteries)) return;

  let battery = batteries.find(
    (entry) => Number(entry.battery_number) === batteryNumber
  );
  return get(battery, "battery_family");
};

let pageReportsFamily = (reports, path) =>
  reports.some((report) => Boolean(get(report, path)));

let resolveBatteryFamilies = (
  reports,
  deviceHealth,
  useDeviceHealthFallback
) => {
  let hasReportedFamily = pageReportsFamily(
    reports,
    "data.statistics.battery_family"
  );
  let hasReportedFamily2 = pageReportsFamily(
    reports,
    "data.statistics.battery_family_2"
  );
  let batteryFamily =
    useDeviceHealthFallback && !hasReportedFamily
      ? familyFromHealth(deviceHealth, 1)
      : undefined;
  let batteryFamily2 =
    useDeviceHealthFallback && !hasReportedFamily2
      ? familyFromHealth(deviceHealth, 2)
      : undefined;
  let resolvedFamilies = new Array(reports.length);

  // Reports arrive newest first. Walk them chronologically so sparse family
  // metadata is carried forward until a later report identifies a swap. Do
  // not backfill an older report from a family observed after that report.
  for (let index = reports.length - 1; index >= 0; index -= 1) {
    let reportedFamily = get(reports[index], "data.statistics.battery_family");
    let reportedFamily2 = get(
      reports[index],
      "data.statistics.battery_family_2"
    );

    if (reportedFamily) batteryFamily = reportedFamily;
    if (reportedFamily2) batteryFamily2 = reportedFamily2;

    resolvedFamilies[index] = { batteryFamily, batteryFamily2 };
  }

  return resolvedFamilies;
};

let BatteryChartModel = {
  percentages: null,
  errors: null,
  isDPD: null,
  isLPD: null,
  batteryCharge: 0,
  batteryCharge2: null,
  has2ndBattery: false,
  init: function (report, date, batteryFamilies) {
    this.date = date;
    this.hasRawSoc = has(report, "data.status.battery_raw_soc");
    this.hasRawSoc2 = has(report, "data.status.battery_raw_soc_2");
    this.isQrbpV2 = isQrbpV2(batteryFamilies.batteryFamily);
    this.isQrbpV2SecondBattery = isQrbpV2(batteryFamilies.batteryFamily2);
    this.has2ndBattery =
      has(report, "data.status.battery_percentage_2") ||
      (this.isQrbpV2SecondBattery && this.hasRawSoc2);
    this.i2cErrors = parseInt(get(report, "data.status.i2c_errors", 0));
    this.isDPD = report.data.status.hasOwnProperty("battery_percentage");
    this.isLPD = !this.isDPD;
    this.battery_percentage = parseInt(
      get(report, "data.status.battery_percentage", 0)
    );
    this.battery_percentage_2 = parseInt(
      get(report, "data.status.battery_percentage_2", 0)
    );
    this.battery_raw_soc = parseInt(
      get(report, "data.status.battery_raw_soc", 0)
    );
    this.battery_raw_soc_2 = parseInt(
      get(report, "data.status.battery_raw_soc_2", 0)
    );
    this.battery_voltage = parseFloat(
      get(report, "data.status.battery_voltage", 0)
    );
    this.battery_voltage_2 = parseFloat(
      get(report, "data.status.battery_voltage_2", 0)
    );
    this.setBatteryValue();
    this.set2ndBatteryCharge();
    return this;
  },
  addToBatteryErrorsChart(chartPoints) {
    if (this.hasErrors())
      chartPoints.unshift({
        y: 0,
        name: "Fuel Guage Error: " + this.battery_percentage,
      });
    else chartPoints.unshift({ y: null });
  },
  addToBatteryChart(chartPoints) {
    let charge = this.hasErrors() ? null : this.batteryCharge;
    let node = { name: this.date, y: parseFloat(charge) };
    chartPoints.unshift(node);
  },
  addTo2ndBatteryChart(chartPoints) {
    if (!this.has2ndBattery) return;
    let node = { name: this.date, y: parseFloat(this.batteryCharge2) };
    chartPoints.unshift(node);
  },
  setBatteryValue: function () {
    if (this.isQrbpV2 && this.hasRawSoc) {
      this.batteryCharge = this.battery_raw_soc;
    } else if (this.isDPD) {
      this.batteryCharge = this.battery_percentage;
    } else if (this.isLPD) {
      this.batteryCharge = this.voltageToPercentage(this.battery_voltage);
    }
  },
  hasErrors() {
    return this.errorReported() && this.errorEncodedInBattery();
  },
  errorEncodedInBattery() {
    return this.battery_percentage >= 200 && this.battery_percentage <= 300;
  },
  errorReported() {
    return (this.i2cErrors & 2) == 2 || (this.i2cErrors & 4) == 4;
  },
  voltageToPercentage(value) {
    let voltage = parseFloat(value || 0);
    let calculated_percentage = Math.round(voltage * 0.143 - 485);
    return Math.min(Math.max(calculated_percentage, 0), 100);
  },
  set2ndBatteryCharge() {
    if (!this.has2ndBattery) return;
    if (this.isQrbpV2SecondBattery && this.hasRawSoc2) {
      this.batteryCharge2 = this.battery_raw_soc_2;
    } else if (this.isDPD) {
      let charge = parseInt(this.battery_percentage_2);
      this.batteryCharge2 = charge <= 100 ? charge : 0;
    } else if (this.isLPD) {
      this.batteryCharge2 = this.voltageToPercentage(this.battery_voltage_2);
    }
  },
};

let initialModels = {
  isLPD: false,
  categories: [],
  stream: [],
  rssi: [],
  battery: [],
  battery2: [],
  batteryErrors: [],
};
let models = {};

function getChartModels(reports, options = {}) {
  models = cloneDeep(initialModels);
  let familyReports = options.familyReports || reports;
  let resolvedFamilies = resolveBatteryFamilies(
    familyReports,
    options.deviceHealth || {},
    options.useDeviceHealthFallback === true
  );
  reports.forEach((report) => {
    let date = ephocDate(report.data.status.time);
    date = moment(date).format("MM/DD/YYYY HH:mm:ss:SSS");
    models.categories.unshift(date);
    let familyReportIndex = familyReports.indexOf(report);
    let batteryFamilies = resolvedFamilies[familyReportIndex] || {
      batteryFamily: get(report, "data.statistics.battery_family"),
      batteryFamily2: get(report, "data.statistics.battery_family_2"),
    };
    let batteryChart = Object.create(BatteryChartModel).init(
      report,
      date,
      batteryFamilies
    );
    batteryChart.addToBatteryChart(models.battery);
    batteryChart.addTo2ndBatteryChart(models.battery2);
    batteryChart.addToBatteryErrorsChart(models.batteryErrors);
  });
  models.rssi = getRSSIChartModel(reports);
  models.stream = getStreamChartModel(reports);
  return models;
}

export default getChartModels;
